import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Calculator, TrendingUp, Building2, Briefcase, Stethoscope, CreditCard,
  Hospital, ShieldCheck, Database, Users, Sparkles, Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = {
  midsize_home_health: {
    label: 'Mid-size Home Health Agency',
    description: '300 patients · 30 clinicians · $250K/mo collections',
    inputs: {
      provider_tier: 'growth', active_patients: 300,
      clinical_users: 30, back_office_users: 5,
      monthly_1099_visits: 850, avg_visit_value_usd: 150,
      rcm_mode: 'full_service', monthly_collections_usd: 250000,
    },
  },
  enterprise_snf: {
    label: 'Enterprise SNF System (3 facilities)',
    description: '600 beds · 120 clinicians · multi-location',
    inputs: {
      provider_tier: 'enterprise', active_patients: 540,
      clinical_users: 120, back_office_users: 15,
      monthly_1099_visits: 300, avg_visit_value_usd: 180,
      rcm_mode: 'saas', rendering_providers: 18,
    },
  },
  hospital_system: {
    label: 'Regional Hospital System',
    description: '500 beds · 4,800 discharges/mo (40% routed)',
    inputs: {
      hospital_beds: 500, monthly_discharges: 4800, discharges_routed_pct: 40,
    },
  },
  ma_payer: {
    label: 'Medicare Advantage Plan',
    description: '200K managed lives · advanced VBC tier',
    inputs: {
      payer_tier: 'ma_advanced', payer_managed_lives: 200000,
    },
  },
  full_network: {
    label: '🚀 Full network (Year 5 mid-case)',
    description: '500 providers + 25 hospitals + 12 payers + data products',
    inputs: {
      provider_tier: 'growth', active_patients: 150000,             // 500 × ~300
      clinical_users: 15000, back_office_users: 2500,
      monthly_1099_visits: 425000, avg_visit_value_usd: 150,
      rcm_mode: 'both', rendering_providers: 9000, monthly_collections_usd: 125000000,
      hospital_beds: 12500, monthly_discharges: 120000, discharges_routed_pct: 35,
      payer_tier: 'ma_advanced', payer_managed_lives: 2400000,
      data_benchmark_subs: 80, data_pharma_contracts: 6, data_rwe_contracts: 3, data_hospital_dashboards: 25,
      monthly_private_pay_bookings: 6000, avg_booking_value_usd: 220, premium_members: 35000,
    },
  },
};

const LAYER_META = {
  provider_platform:  { label: 'Provider Platform',   icon: Briefcase,    color: 'bg-cn-primary text-white' },
  per_seat:           { label: 'Per-Seat SaaS',       icon: Users,        color: 'bg-cn-accent text-white' },
  marketplace:        { label: 'Marketplace',         icon: Stethoscope,  color: 'bg-purple-600 text-white' },
  rcm:                { label: 'Revenue Cycle',       icon: CreditCard,   color: 'bg-emerald-600 text-white' },
  hospital_platform:  { label: 'Hospital Platform',   icon: Hospital,     color: 'bg-slate-700 text-white' },
  payer_vbc:          { label: 'Payer VBC (PEPM)',    icon: ShieldCheck,  color: 'bg-amber-600 text-white' },
  data_network:       { label: 'Data Network',        icon: Database,     color: 'bg-rose-600 text-white' },
  family:             { label: 'Family / Consumer',   icon: Users,        color: 'bg-teal-600 text-white' },
};

const fmtUSD = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export default function PricingCalculator() {
  const [inputs, setInputs] = useState(PRESETS.midsize_home_health.inputs);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState('midsize_home_health');

  const calculate = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/pricing/v2/calculate`, inputs);
      setResult(data);
    } finally {
      setLoading(false);
    }
  }, [inputs]);

  useEffect(() => { calculate(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Debounced recompute on input change.
  useEffect(() => {
    const t = setTimeout(calculate, 250);
    return () => clearTimeout(t);
  }, [inputs, calculate]);

  const setInput = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  const applyPreset = (key) => {
    setActivePreset(key);
    setInputs(PRESETS[key].inputs);
  };

  const layerKeys = useMemo(() => Object.keys(result?.layers || {}), [result]);

  return (
    <>
      <Seo title="Pricing Calculator — CaraLoom"
           description="See how the 8-layer monetization stack of CaraLoom maps to your organization's ARR contribution. Real-time. No sales call required." />
      <PublicNavbar />
      <main className="min-h-screen bg-slate-50" data-testid="pricing-calculator-page">
        <header className="bg-gradient-to-br from-slate-900 to-cn-primary-hover text-white">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cn-accent mb-3">
              <Calculator className="w-4 h-4" /> CaraLoom Pricing v2
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl">
              The 8-layer monetization stack.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 mt-4 max-w-2xl">
              CaraLoom prices to your buyer's economics — per-active-patient, per-seat, per-managed-life,
              per-claim. See your exact ARR contribution in 60 seconds.
            </p>
          </div>
        </header>

        <section className="max-w-7xl mx-auto px-6 py-10">
          {/* Preset row */}
          <div className="flex flex-wrap gap-3 mb-8" data-testid="preset-row">
            {Object.entries(PRESETS).map(([k, p]) => (
              <button
                key={k}
                onClick={() => applyPreset(k)}
                data-testid={`preset-${k}`}
                className={`text-left px-4 py-3 rounded-2xl border transition ${
                  activePreset === k
                    ? 'border-cn-primary bg-white shadow-md'
                    : 'border-slate-200 bg-white/60 hover:bg-white'
                }`}
              >
                <div className="text-sm font-semibold text-slate-900">{p.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.description}</div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* LEFT — inputs (3/5) */}
            <div className="lg:col-span-3 space-y-5">
              {/* Provider Platform */}
              <Card title="1 — Provider Platform" icon={<Briefcase className="w-4 h-4" />}>
                <Row>
                  <Select label="Tier" testid="provider-tier" value={inputs.provider_tier || ''}
                          onChange={(v) => setInput('provider_tier', v || null)}
                          options={[
                            { v: '', l: '— off —' },
                            { v: 'essentials', l: 'Essentials · $7 PMPM' },
                            { v: 'growth',     l: 'Growth · $11 PMPM' },
                            { v: 'enterprise', l: 'Enterprise · $18 PMPM' },
                          ]} />
                  <Num label="Active patients" testid="active-patients"
                       value={inputs.active_patients} onChange={(v) => setInput('active_patients', v)} />
                </Row>
              </Card>

              {/* Per-seat */}
              <Card title="2 — Per-Seat SaaS" icon={<Users className="w-4 h-4" />}>
                <Row>
                  <Num label="Clinical users ($99/mo)" testid="clinical-users"
                       value={inputs.clinical_users} onChange={(v) => setInput('clinical_users', v)} />
                  <Num label="Back-office users ($199/mo)" testid="back-office-users"
                       value={inputs.back_office_users} onChange={(v) => setInput('back_office_users', v)} />
                </Row>
              </Card>

              {/* Marketplace */}
              <Card title="3 — Marketplace Take" icon={<Stethoscope className="w-4 h-4" />}>
                <Row>
                  <Num label="Monthly 1099 visits" testid="visits-1099" value={inputs.monthly_1099_visits} onChange={(v) => setInput('monthly_1099_visits', v)} />
                  <Num label="Avg visit $" testid="avg-visit-value" value={inputs.avg_visit_value_usd} onChange={(v) => setInput('avg_visit_value_usd', v)} />
                </Row>
                <Row>
                  <Num label="W-2 placements / yr" testid="w2-placements" value={inputs.annual_w2_placements} onChange={(v) => setInput('annual_w2_placements', v)} />
                  <Num label="Avg W-2 salary $" testid="w2-salary" value={inputs.avg_w2_first_year_salary_usd} onChange={(v) => setInput('avg_w2_first_year_salary_usd', v)} />
                </Row>
              </Card>

              {/* RCM */}
              <Card title="4 — Revenue Cycle Management" icon={<CreditCard className="w-4 h-4" />}>
                <Row>
                  <Select label="Mode" testid="rcm-mode" value={inputs.rcm_mode || 'none'}
                          onChange={(v) => setInput('rcm_mode', v === 'none' ? null : v)}
                          options={[
                            { v: 'none',         l: '— off —' },
                            { v: 'saas',         l: 'SaaS · $499/provider/mo' },
                            { v: 'full_service', l: 'Full-service · 4.5% of collections' },
                            { v: 'both',         l: 'Both' },
                          ]} />
                  <Num label="Rendering providers" testid="rendering-providers" value={inputs.rendering_providers} onChange={(v) => setInput('rendering_providers', v)} />
                </Row>
                <Row>
                  <Num label="Monthly collections $" testid="monthly-collections" value={inputs.monthly_collections_usd} onChange={(v) => setInput('monthly_collections_usd', v)} />
                </Row>
              </Card>

              {/* Hospital */}
              <Card title="5 — Hospital Discharge Platform" icon={<Hospital className="w-4 h-4" />}>
                <Row>
                  <Num label="Hospital beds ($8 PBPM)" testid="hospital-beds" value={inputs.hospital_beds} onChange={(v) => setInput('hospital_beds', v)} />
                  <Num label="Monthly discharges" testid="monthly-discharges" value={inputs.monthly_discharges} onChange={(v) => setInput('monthly_discharges', v)} />
                </Row>
                <Row>
                  <Num label="% routed through CaraLoom" testid="discharges-routed-pct" value={inputs.discharges_routed_pct} onChange={(v) => setInput('discharges_routed_pct', v)} max={100} />
                </Row>
              </Card>

              {/* Payer VBC */}
              <Card title="6 — Payer Value-Based Care (PEPM)" icon={<ShieldCheck className="w-4 h-4" />}>
                <Row>
                  <Select label="Payer tier" testid="payer-tier" value={inputs.payer_tier || ''}
                          onChange={(v) => setInput('payer_tier', v || null)}
                          options={[
                            { v: '', l: '— off —' },
                            { v: 'ma_essentials', l: 'MA Essentials · $4.50 PEPM' },
                            { v: 'ma_advanced',   l: 'MA Advanced · $8.50 PEPM' },
                            { v: 'ma_premium',    l: 'MA Premium · $14 PEPM' },
                          ]} />
                  <Num label="Managed lives" testid="payer-lives" value={inputs.payer_managed_lives} onChange={(v) => setInput('payer_managed_lives', v)} />
                </Row>
              </Card>

              {/* Data + Family */}
              <Card title="7 — Data Network + 8 — Family" icon={<Database className="w-4 h-4" />}>
                <Row>
                  <Num label="Benchmark subscriptions ($150K/yr)" testid="data-benchmark" value={inputs.data_benchmark_subs} onChange={(v) => setInput('data_benchmark_subs', v)} />
                  <Num label="Pharma contracts ($1.5M/yr)" testid="data-pharma" value={inputs.data_pharma_contracts} onChange={(v) => setInput('data_pharma_contracts', v)} />
                </Row>
                <Row>
                  <Num label="Private-pay bookings/mo" testid="family-bookings" value={inputs.monthly_private_pay_bookings} onChange={(v) => setInput('monthly_private_pay_bookings', v)} />
                  <Num label="Premium members ($39/mo)" testid="family-members" value={inputs.premium_members} onChange={(v) => setInput('premium_members', v)} />
                </Row>
              </Card>
            </div>

            {/* RIGHT — results (2/5) */}
            <aside className="lg:col-span-2 lg:sticky lg:top-24 self-start space-y-4" data-testid="results-panel">
              {/* HEADLINE ARR */}
              <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-cn-primary-hover text-white p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cn-accent mb-2">
                  <Sparkles className="w-4 h-4" /> Annual Contract Value
                </div>
                <div className="text-4xl font-extrabold tabular-nums" data-testid="results-total-arr">
                  {fmtUSD(result?.totals?.annual)}
                </div>
                <div className="text-xs text-slate-300 mt-2">
                  Monthly recurring · {fmtUSD(result?.totals?.monthly)}
                  {result?.totals?.active_layer_count > 0 && (
                    <> · {result.totals.active_layer_count} active layer{result.totals.active_layer_count > 1 ? 's' : ''}</>
                  )}
                </div>
              </div>

              {/* LAYER BREAKDOWN */}
              {layerKeys.length > 0 && (
                <div className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="results-layers">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Layer mix</div>
                  <ul className="space-y-2">
                    {layerKeys.map((k) => {
                      const meta = LAYER_META[k] || { label: k };
                      const Icon = meta.icon || Info;
                      const layer = result.layers[k];
                      const pct = result.totals.mix_pct[k] || 0;
                      return (
                        <li key={k} data-testid={`layer-${k}`} className="flex items-center gap-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${meta.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-sm font-semibold text-slate-900 truncate">{meta.label}</span>
                              <span className="text-sm font-bold tabular-nums">{fmtUSD(layer.annual)}</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                              <div className={`h-full ${meta.color}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{pct}% of total</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* 5-YEAR PROJECTION */}
              {result?.five_year_arr?.length > 0 && (
                <div className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="results-5yr">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
                    <TrendingUp className="w-3 h-3" /> 5-year ARR projection
                    <span className="ml-auto text-[10px] text-slate-400">30% YoY · 22% from yr 4</span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {result.five_year_arr.map((r) => (
                        <tr key={r.year} className="border-b border-slate-100 last:border-0">
                          <td className="py-1.5 text-slate-600">Year {r.year}</td>
                          <td className="py-1.5 text-right font-semibold tabular-nums">{fmtUSD(r.arr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* VALUATION ESTIMATE */}
              {result?.valuation_estimate && result.totals.annual > 0 && (
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5" data-testid="results-valuation">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 mb-1">Valuation range</div>
                  <div className="text-xl font-extrabold text-amber-900 tabular-nums">
                    {fmtUSD(result.valuation_estimate.low_valuation)} – {fmtUSD(result.valuation_estimate.high_valuation)}
                  </div>
                  <div className="text-[10px] text-amber-700 mt-1">
                    {result.valuation_estimate.low_multiple}x – {result.valuation_estimate.high_multiple}x ARR
                  </div>
                </div>
              )}

              <Button asChild className="w-full bg-cn-primary hover:bg-cn-primary-hover text-white">
                <a href="/contact" data-testid="results-talk-cta">Talk to a CaraLoom partner →</a>
              </Button>
            </aside>
          </div>

          {/* Footnote */}
          <div className="mt-12 text-xs text-slate-500 max-w-3xl">
            <Info className="w-3 h-3 inline mr-1" />
            Rate card v{result?.rate_card_version || '—'}. The 5-year projection assumes 30% YoY for years 1-3 and 22% for years 4-5,
            anchored to public B2B SaaS benchmarks. Valuation range uses 12-28x ARR — the spread reflects the
            difference between transactional-only and the data + payer-anchored multi-layer mix.
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}

// ── inputs ───────────────────────────────────────────────────────────────

function Card({ title, icon, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-cn-primary/10 text-cn-primary">{icon}</span>
        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Num({ label, value, onChange, testid, max }) {
  const onChangeHandler = (e) => {
    const v = e.target.value;
    onChange(v === '' ? null : Math.min(max ?? Number.MAX_SAFE_INTEGER, Number(v)));
  };
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <input
        type="number"
        min="0"
        value={value ?? ''}
        onChange={onChangeHandler}
        data-testid={`input-${testid}`}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cn-primary tabular-nums"
      />
    </label>
  );
}

function Select({ label, value, onChange, testid, options }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`input-${testid}`}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-cn-primary"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
